// TFVF Home — the staff-facing "My Training" page.
// Shows:
//   - Current-action card at the top
//   - Overall progress
//   - Role-based module list with completion state
//   - Acknowledgement CTA if unsigned for the current version
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, Circle, Clock, ArrowRight, ShieldCheck, RotateCcw, AlertTriangle } from 'lucide-react';
import { API, authHeaders, pct, ROLE_LABELS } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const TfvfHome = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/tfvf/me`, { headers: authHeaders(token) })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;
  if (!data) return <p className="text-[color:var(--tf-muted)]">Failed to load training home.</p>;

  const action = data.current_action;

  return (
    <div className="space-y-6" data-testid="tfvf-home">
      {/* Current action */}
      {action && (
        <div className="tf-card rounded-xl p-5 flex items-start gap-4 border-l-4" style={{ borderLeftColor: 'var(--tf-gold)' }} data-testid="tfvf-current-action">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(200,151,74,0.15)' }}>
            <ArrowRight className="w-5 h-5" style={{ color: 'var(--tf-gold)' }} />
          </div>
          <div className="flex-1">
            <p className="tf-eyebrow">Next action</p>
            <p className="text-lg font-semibold text-[color:var(--tf-forest)] mt-0.5">{action.cta}</p>
          </div>
          {action.type === 'complete_module' && (
            <Link to={`/training/${action.module_id}`} className="tf-btn-primary px-4 py-2 rounded-lg text-sm font-semibold" data-testid="tfvf-action-cta">
              Open module
            </Link>
          )}
          {action.type === 'recert_module' && (
            <Link to={`/training/${action.module_id}`} className="tf-btn-gold px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5" data-testid="tfvf-action-cta">
              <RotateCcw className="w-3.5 h-3.5" /> Retake quiz
            </Link>
          )}
          {action.type === 'sign_acknowledgements' && (
            <Link to="/training/acknowledgements" className="tf-btn-gold px-4 py-2 rounded-lg text-sm font-semibold" data-testid="tfvf-action-cta">
              Sign now
            </Link>
          )}
        </div>
      )}

      {/* Recertification banner */}
      {data.recert_count > 0 && (
        <div
          className="rounded-xl p-4 flex items-start gap-3 border"
          style={{ background: 'rgba(200,151,74,0.10)', borderColor: 'rgba(200,151,74,0.35)' }}
          data-testid="tfvf-recert-banner"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'var(--tf-gold-2)' }} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-[color:var(--tf-forest)]">
              {data.recert_count === 1
                ? '1 module has been updated — recertification required'
                : `${data.recert_count} modules have been updated — recertification required`}
            </p>
            <p className="text-xs text-[color:var(--tf-muted)] mt-0.5">
              Content, quiz questions, or approved language changed since you last certified. Retake the quiz to keep your certification current.
            </p>
          </div>
        </div>
      )}

      {/* Progress + acknowledgement summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="tf-card rounded-xl p-5" data-testid="tfvf-progress-card">
          <p className="tf-eyebrow">Overall completion</p>
          <p className="text-3xl font-bold text-[color:var(--tf-forest)] mt-1">{pct(data.completion_pct)}</p>
          <div className="tf-progress-track h-2 rounded-full mt-3 overflow-hidden">
            <div className="tf-progress-fill h-full" style={{ width: `${data.completion_pct}%` }} />
          </div>
          <p className="text-xs text-[color:var(--tf-muted)] mt-2">
            {data.completed_count} of {data.total_count} modules complete
            {' · '}{data.completed_required}/{data.total_required} required
          </p>
        </div>

        <div className="tf-card rounded-xl p-5">
          <p className="tf-eyebrow">Learning path</p>
          <p className="text-lg font-semibold text-[color:var(--tf-forest)] mt-1">{ROLE_LABELS[data.path_key] || data.path_key}</p>
          <p className="text-xs text-[color:var(--tf-muted)] mt-1">{data.total_count} modules · minimum 85% knowledge / 70% practical to certify</p>
        </div>

        <div className="tf-card rounded-xl p-5" data-testid="tfvf-ack-card">
          <p className="tf-eyebrow">Acknowledgements</p>
          {data.acknowledgement_program_version_signed ? (
            <>
              <p className="text-lg font-semibold text-emerald-700 mt-1 inline-flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> Signed v{data.acknowledgement_program_version_signed}
              </p>
              <p className="text-xs text-[color:var(--tf-muted)] mt-1">Re-sign only when the platform program version changes.</p>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold text-amber-700 mt-1">Not yet signed</p>
              <Link to="/training/acknowledgements" className="text-xs text-[color:var(--tf-gold-2)] font-semibold hover:underline mt-2 inline-block">
                Review & sign →
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Module list */}
      <section>
        <h2 className="text-lg font-bold text-[color:var(--tf-forest)] mb-3">Your modules</h2>
        <div className="space-y-2">
          {data.modules.map((m, i) => {
            const done = !!m.progress?.completed_at;
            const viewed = !!m.progress?.viewed_at;
            const recert = !!m.progress?.recert_required;
            return (
              <Link
                key={m.module_id}
                to={`/training/${m.module_id}`}
                className={`tf-card rounded-lg p-4 flex items-center gap-4 hover:shadow-md transition-shadow ${recert ? 'ring-1' : ''}`}
                style={recert ? { boxShadow: 'inset 0 0 0 1px rgba(200,151,74,0.55)' } : undefined}
                data-testid={`tfvf-module-${m.module_id}`}
              >
                <div className="w-8 shrink-0 text-center">
                  {recert ? <RotateCcw className="w-6 h-6 mx-auto" style={{ color: 'var(--tf-gold-2)' }} data-testid={`tfvf-recert-icon-${m.module_id}`} />
                    : done ? <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto" />
                    : viewed ? <Clock className="w-6 h-6 text-amber-500 mx-auto" />
                    : <Circle className="w-6 h-6 text-[color:var(--tf-forest)]/25 mx-auto" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="tf-eyebrow">{m.module_id}</span>
                    {m.required && <span className="text-[10px] uppercase font-bold text-[color:var(--tf-gold-2)]">Required</span>}
                    {recert && (
                      <span
                        className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(200,151,74,0.18)', color: 'var(--tf-gold-2)' }}
                        data-testid={`tfvf-recert-badge-${m.module_id}`}
                      >
                        Recert required
                      </span>
                    )}
                    <span className="text-[10px] text-[color:var(--tf-muted)]">· {m.duration_minutes}min</span>
                  </div>
                  <p className="text-base font-semibold text-[color:var(--tf-forest)] truncate">{m.title}</p>
                  <p className="text-xs text-[color:var(--tf-muted)] truncate">{m.summary}</p>
                </div>
                <div className="text-right shrink-0">
                  {m.progress?.quiz_score != null && (
                    <p className="text-xs text-[color:var(--tf-muted)]">Best: <span className={`font-bold ${m.progress.quiz_passed ? 'text-emerald-700' : 'text-amber-700'}`}>{m.progress.quiz_score}%</span></p>
                  )}
                  <p className="text-xs text-[color:var(--tf-forest)]/60 font-medium">Step {i + 1}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default TfvfHome;
